import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react";
import React from "react";

/**
 * Read-only survey chart for the generated-document review editor.
 *
 * Generated survey charts are deterministic base64 images carrying the
 * `mps-survey-chart` class (see ai-service survey_chart_service._svg_to_img).
 * In review they should be a frozen preview — not resized/edited like a normal
 * image — consistent with the other data-driven blocks. This node claims those
 * images (higher parse priority than the editable Image node), renders them
 * read-only, and serialises them back to the identical <img> so the chart
 * reaches the saved PDF unchanged.
 *
 * Only register this in the review editor; the template designer keeps the
 * editable SurveyChart marker node.
 */

const FrozenChartComponent = ({ node }) => {
  const { src, alt, width, height } = node.attrs;
  return (
    <NodeViewWrapper
      as="span"
      className="frozen-survey-chart"
      contentEditable={false}
      title="Generated from patient data"
      style={{ cursor: "default", display: "inline-block" }}
    >
      <img src={src} alt={alt || ""} width={width || undefined} height={height || undefined} />
    </NodeViewWrapper>
  );
};

export const FrozenChart = Node.create({
  name: "frozenChart",
  group: "inline",
  inline: true,
  atom: true,
  draggable: false,
  selectable: false,
  // Beat the editable Image node for chart images specifically.
  priority: 1000,

  addAttributes() {
    return {
      src: { default: null, parseHTML: (el) => el.getAttribute("src") },
      alt: { default: "", parseHTML: (el) => el.getAttribute("alt") || "" },
      width: { default: null, parseHTML: (el) => el.getAttribute("width") },
      height: { default: null, parseHTML: (el) => el.getAttribute("height") },
    };
  },

  parseHTML() {
    return [{ tag: "img.mps-survey-chart", priority: 1000 }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["img", mergeAttributes(HTMLAttributes, { class: "mps-survey-chart" })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(FrozenChartComponent);
  },
});

export default FrozenChart;
